import { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import DateTimePicker from "../../components/DateTimePicker";
import {
  createFollowUpDiscussion,
  updateFollowUpPreliminary,
  getFollowUpDiscussionDetails,
  getFollowUpNextLeadNumber,
  CreateFollowUpDiscussionPayload,
  UpdateFollowUpPreliminaryPayload,
} from "../../api/followUpDiscussion";
import { toast } from "sonner";
import { normalizePhone, formatPhoneInput } from "../../utils/phone";

const CREATE_FIELDS: (keyof CreateFollowUpDiscussionPayload)[] = [
  "markName",
  "contactName",
  "phone",
  "email",
  "callTime",
];

function pickCreatePayload(values: Record<string, unknown>): CreateFollowUpDiscussionPayload {
  const payload: Record<string, unknown> = {};
  for (const key of CREATE_FIELDS) {
    const v = values[key];
    if (key === "email" && (v === undefined || v === "")) continue;
    if (key === "callTime" && v instanceof Date) {
      payload[key] = v.toISOString();
      continue;
    }
    if (v !== undefined && v !== null && v !== "") {
      payload[key] = v;
    }
  }
  return payload as unknown as CreateFollowUpDiscussionPayload;
}

const UPDATE_FIELDS: (keyof UpdateFollowUpPreliminaryPayload)[] = [
  "markName",
  "contactName",
  "phone",
  "email",
  "callTime",
];

function pickUpdatePayload(values: Record<string, unknown>): UpdateFollowUpPreliminaryPayload {
  const payload: Record<string, unknown> = {};
  for (const key of UPDATE_FIELDS) {
    const v = values[key];
    if (key === "email" && (v === undefined || v === "")) continue;
    if (key === "callTime" && v instanceof Date) {
      payload[key] = v.toISOString();
      continue;
    }
    if (v !== undefined && v !== null && v !== "") {
      payload[key] = v;
    }
  }
  return payload as unknown as UpdateFollowUpPreliminaryPayload;
}

const validationSchema = Yup.object({
  leadNo: Yup.string().required("Required"),
  markName: Yup.string().required("Required"),
  contactName: Yup.string().required("Required"),
  phone: Yup.string()
    .required("WhatsApp Number is required")
    .test(
      "mobile",
      "Enter a valid mobile number (10-15 digits, optional + prefix)",
      (value) => {
        if (!value?.trim()) return false;
        const digits = normalizePhone(value);
        return digits.length >= 10 && digits.length <= 15;
      }
    ),
  email: Yup.string().email("Invalid email").optional(),
  callTime: Yup.date()
    .required("Time & Date of call is required")
    .nullable()
    .typeError("Please select a valid date and time"),
});

type FormValues = {
  leadNo: string;
  markName: string;
  contactName: string;
  phone: string;
  email: string;
  callTime: Date | null;
};

const initialValues: FormValues = {
  leadNo: "",
  markName: "",
  contactName: "",
  phone: "",
  email: "",
  callTime: null,
};

type FollowUpPreliminaryDiscussionViewProps = {
  leadId?: string;
  onSuccess?: (leadId: string) => void;
};

export const FOLLOW_UP_PRELIMINARY_FORM_ID = "follow-up-preliminary-form";

export default function FollowUpPreliminaryDiscussionView({
  leadId,
  onSuccess,
}: FollowUpPreliminaryDiscussionViewProps) {
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadedValues, setLoadedValues] = useState<FormValues | null>(null);

  useEffect(() => {
    if (leadId) {
      let cancelled = false;
      setLoadError(null);
      getFollowUpDiscussionDetails(leadId)
        .then((data) => {
          if (cancelled) return;
          setLoadedValues({
            leadNo: data.leadNo ?? "",
            markName: data.preliminary.markName ?? "",
            contactName: data.preliminary.contactName ?? "",
            phone: data.preliminary.phone ?? "",
            email: data.preliminary.email ?? "",
            callTime: data.preliminary.callTime
              ? new Date(data.preliminary.callTime)
              : null,
          });
        })
        .catch((err) => {
          if (cancelled) return;
          const message = err instanceof Error ? err.message : "Failed to load lead";
          toast.error(message);
          setLoadError(message);
        });
      return () => {
        cancelled = true;
      };
    }
    let cancelled = false;
    setLoadError(null);
    setLoadedValues(null);
    getFollowUpNextLeadNumber()
      .then(({ leadNo }) => {
        if (!cancelled) setLoadedValues({ ...initialValues, leadNo });
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : "Failed to get lead number";
        toast.error(message);
        setLoadError(message);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const startValues = loadedValues ?? initialValues;
  const isReady = loadedValues !== null || loadError !== null;

  if (!isReady) {
    return (
      <div className="max-w-[1085px] flex flex-col gap-5">
        <div className="text-xs text-[#6D6D6D]">
          {leadId ? "Loading lead details..." : "Loading next lead number..."}
        </div>
      </div>
    );
  }

  return (
    <Formik
      key={leadId ?? "new"}
      initialValues={startValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={async (values, { setStatus, setSubmitting }) => {
        try {
          if (leadId) {
            const payload = pickUpdatePayload(
              values as unknown as Record<string, unknown>
            );
            await updateFollowUpPreliminary(leadId, payload);
            toast.success("Preliminary discussion saved successfully.");
            setStatus({ type: "success", message: "Discussion updated successfully." });
            onSuccess?.(leadId);
          } else {
            const payload = pickCreatePayload(
              values as unknown as Record<string, unknown>
            );
            const data = await createFollowUpDiscussion(payload);
            toast.success("Preliminary discussion started successfully.");
            setStatus({ type: "success", message: "Discussion started successfully." });
            onSuccess?.(data.leadId);
          }
        } catch (err) {
          const message =
            err instanceof Error
              ? err.message
              : leadId
                ? "Failed to update discussion."
                : "Failed to create discussion.";
          toast.error(message);
          setStatus({ type: "error", message });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ setFieldValue, values, status }) => (
        <Form
          id={FOLLOW_UP_PRELIMINARY_FORM_ID}
          className="max-w-[1085px] flex flex-col gap-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-5">
            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-normal text-[#6D6D6D] leading-5">
                Lead No.
              </label>
              <Field
                name="leadNo"
                type="text"
                placeholder="Text Here"
                readOnly
                className="w-full px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D] bg-white cursor-not-allowed md:bg-[#F5F5F5]"
              />
              <ErrorMessage
                name="leadNo"
                component="div"
                className="text-xs text-red-500 mt-0.5"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-normal text-[#222] leading-5">
                Mark
              </label>
              <Field
                name="markName"
                type="text"
                placeholder="Text Here"
                className="w-full px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]"
              />
              <ErrorMessage
                name="markName"
                component="div"
                className="text-xs text-red-500 mt-0.5"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-5">
            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-normal text-[#222] leading-5">
                Contact Name
              </label>
              <Field
                name="contactName"
                type="text"
                placeholder="Text Here"
                className="w-full px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]"
              />
              <ErrorMessage
                name="contactName"
                component="div"
                className="text-xs text-red-500 mt-0.5"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-normal text-[#222] leading-5">
                WhatsApp Number
              </label>
              <Field name="phone">
                {({
                  field,
                  form,
                }: {
                  field: { name: string; value: string };
                  form: { setFieldValue: (n: string, v: string) => void };
                }) => (
                  <input
                    {...field}
                    type="tel"
                    inputMode="numeric"
                    placeholder="Enter WhatsApp Number"
                    className="w-full px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]"
                    onChange={(e) => {
                      form.setFieldValue("phone", formatPhoneInput(e.target.value));
                    }}
                  />
                )}
              </Field>
              <ErrorMessage
                name="phone"
                component="div"
                className="text-xs text-red-500 mt-0.5"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-5">
            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-normal text-[#222] leading-5">
                Email Address
              </label>
              <Field
                name="email"
                type="email"
                placeholder="Email Address"
                className="w-full px-[10px] py-2 text-[12px] border border-[#EEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]"
              />
              <ErrorMessage
                name="email"
                component="div"
                className="text-xs text-red-500 mt-0.5"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-normal text-[#222] leading-5">
                Time & Date of call
              </label>
              <DateTimePicker
                value={values.callTime}
                onChange={(date) => setFieldValue("callTime", date)}
                showTime
                placeholder="Select Date"
                buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
                minDate={(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })()}
              />
              <ErrorMessage
                name="callTime"
                component="div"
                className="text-xs text-red-500 mt-0.5"
              />
            </div>
          </div>
          {status?.type === "success" && (
            <div className="text-xs text-green-600">{status.message}</div>
          )}
        </Form>
      )}
    </Formik>
  );
}
